import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import KanseiBreadcrumbs from 'components/breadcrumb'
import KanseiDialog from 'components/dialog'
import KanseiSimpleSelectBox from 'components/simpleSelectBox'
import KanseiTextField from 'components/textField'
import { companyApi } from 'hooks/api/company'
import { NewDate } from 'lib/helper/day'
import { Company } from 'types/company'

import CompanyBranches from './branch'

interface Form {
  address: string
  corporateNumber: string
  faxNumber: string
  invoiceRegistrationNumber: string
  name: string
  phoneNumber: string
  postalCode: string
  representativeName: string
}

// プロバイダーが今後拡張したときのための配列
const PROVIDER_OPTIONS = [
  { label: '警備pro', value: '1' },
  { label: 'KeiBiz', value: '2' },
]

export default function DetailPage() {
  const [company, setCompany] = useState<Company>()
  const [providerSelectOpen, setProviderSelectOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<string>('1')
  const { id } = useParams<{ id: string }>()
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Form>()

  const fetchData = () => {
    companyApi()
      .findById(id!)
      .then((res) => {
        reset(res.data)
        setCompany(res.data)
      })
  }

  useEffect(() => {
    fetchData()
  }, [])

  const onSubmit = (form: Form) => {
    const data = {
      ...form,
    } as Company
    companyApi()
      .edit(id!, data)
      .then(() => {
        fetchData()
      })
  }

  const handleConfirmIssue = () => {
    companyApi()
      .issueExternalApiKey(id!, Number(selectedProvider))
      .then((res) => {
        // CSV生成
        const csvContent = `会社名,プロバイダー名,APIキー\n"${company?.name}","${res.data.provider}","${res.data.plainKey}"`

        const reader = new FileReader()
        reader.onload = (e: any) => {
          const tmpLink = document.createElement('a')
          const title = `${company?.name}-external-api-key.csv`
          tmpLink.download = title
          if (typeof e.target.result !== 'string') return
          tmpLink.href = e.target.result
          tmpLink.click()
        }

        const bom = new Uint8Array([0xef, 0xbb, 0xbf])
        const csvBlob = new Blob([bom, csvContent], { type: 'text/csv' })
        reader.readAsDataURL(csvBlob)

        setConfirmOpen(false)
        alert('外部APIキーを発行し、CSVをダウンロードしました')
        fetchData()
      })
      .catch((error) => {
        alert(error?.data?.msg || '外部APIキーの発行に失敗しました')
      })
  }

  const links = [
    {
      title: '警備会社一覧',
      to: '/company',
    },
    {
      title: company ? company.name : '',
    },
  ]

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ '& .MuiTextField-root': { m: 1 }, maxWidth: '500px' }}
    >
      <KanseiBreadcrumbs links={links} style={{ marginBottom: '15px' }} />
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <KanseiTextField
            id="name"
            label="Name"
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <KanseiTextField
            id="corporateNumber"
            label="法人番号"
            control={control}
            errors={errors}
            required
          />
        </Grid>

        <Grid item xs={4}>
          <KanseiTextField
            id="phoneNumber"
            label="電話番号"
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <KanseiTextField
            id="faxNumber"
            label="FAX番号"
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <KanseiTextField
            id="invoiceRegistrationNumber"
            label="登録番号"
            control={control}
            errors={errors}
          />
        </Grid>

        <Grid item xs={4}>
          <KanseiTextField
            id="address"
            label="住所"
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <KanseiTextField
            id="postalCode"
            label="郵便番号"
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={4}>
          <KanseiTextField
            id="representativeName"
            label="代表者名"
            control={control}
            errors={errors}
            required
          />
        </Grid>
      </Grid>
      <Stack
        direction="column"
        spacing={2}
        sx={{ mt: 2, alignItems: 'flex-start' }}
      >
        <Button type="submit" variant="contained">
          更新
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setSelectedProvider('1')
            setProviderSelectOpen(true)
          }}
        >
          外部APIキー発行
        </Button>

        {company?.externalApiKeys && company.externalApiKeys.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              width: '100%',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              発行済み外部APIキー
            </Typography>
            <Stack spacing={1}>
              {company.externalApiKeys.map((key, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2">
                    プロバイダー: {key.provider}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    発行日時: {NewDate(key.madeAt).format('YYYY/MM/DD HH:mm')}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>

      {/* モーダル1: Provider選択 */}
      <KanseiDialog
        open={providerSelectOpen}
        handleClose={() => setProviderSelectOpen(false)}
        maxWidth="sm"
      >
        <Box sx={{ width: '300px', p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            プロバイダー選択
          </Typography>
          <KanseiSimpleSelectBox
            label="プロバイダー"
            options={PROVIDER_OPTIONS}
            value={selectedProvider}
            onChange={(value) => setSelectedProvider(value)}
            fullWidth
            required
          />
          <Box
            sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}
          >
            <Button onClick={() => setProviderSelectOpen(false)}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setProviderSelectOpen(false)
                setConfirmOpen(true)
              }}
            >
              次へ
            </Button>
          </Box>
        </Box>
      </KanseiDialog>

      {/* モーダル2: 発行確認 */}
      <KanseiDialog
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        maxWidth="sm"
      >
        <Box sx={{ width: '400px', p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            外部APIキー発行確認
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            以下の内容で外部APIキーを発行します。
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            会社名: {company?.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            プロバイダー:{' '}
            {PROVIDER_OPTIONS.find((p) => p.value === selectedProvider)?.label}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mb: 3 }}>
            注意:
            このAPIキーは今回のみ表示されます。CSVファイルをダウンロードして、安全な場所に保管してください。
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setConfirmOpen(false)}>キャンセル</Button>
            <Button variant="contained" onClick={handleConfirmIssue}>
              発行する
            </Button>
          </Box>
        </Box>
      </KanseiDialog>

      <CompanyBranches company={company} />
    </Box>
  )
}
